export const superAdminGuide = {
  id: 'super-admin',
  title: 'Guide Super Admin',
  subtitle: 'Administration de la plateforme PunchTM : gestion des tenants, supervision, diagnostic et assistant IA.',
  sections: [
    /* ============================================================
       1. VUE D'ENSEMBLE
    ============================================================ */
    {
      id: 'vue-ensemble',
      title: 'Vue d\'ensemble',
      overview: 'Comprendre le role du Super Admin et la navigation dans la Platform Admin.',
      content: [
        {
          id: 'role-super-admin',
          title: 'Le role du Super Admin',
          description: 'Le Super Admin (Platform Superadmin) est le niveau d\'accès le plus élevé de PunchTM. Il a une vue globale sur tous les tenants (clients) de la plateforme. Son role est d\'assurer le bon fonctionnement technique, de superviser l\'activité des clients et d\'intervenir en cas de probleme.',
          whenToUse: 'Vous etes Super Admin si vous etes responsable de l\'exploitation de la plateforme PunchTM dans son ensemble (pas un seul client).',
          steps: [
            'Connectez-vous avec vos identifiants Super Admin sur la page de connexion.',
            'Vous acccedez automatiquement au tableau de bord d\'administration.',
            'Dans le menu lateral, cliquez sur "Plateforme" pour accéder a la Platform Admin.',
            'Vous y trouverez les onglets : Supervision, Tenants, Diagnostic, Assistant IA et Journal.'
          ],
          screenshot: '/docs/platform-admin-overview.png',
          screenshotAlt: 'Page Platform Admin - Vue d\'ensemble',
          result: 'Vous voyez le tableau de supervision avec les KPIs globaux et l\'état de sante de chaque tenant.',
          tips: [
            'En tant que Super Admin, vous pouvez aussi naviguer dans n\'importe quel module de l\'application (Dashboard, Employés, Paie, etc.) en selectionnant un tenant dans le selecteur en haut du menu lateral.',
            'Le selecteur de tenant vous permet de voir les donnees d\'un client spécifique sans quitter votre session.'
          ],
          errors: [
            { problem: 'Je ne vois pas le menu "Plateforme"', solution: 'Vérifiez que votre compte a bien le role "platform_superadmin". Seul ce role donne accès a la Platform Admin.' },
            { problem: 'Le selecteur de tenant est vide', solution: 'Aucun tenant n\'a ete créé. Allez dans l\'onglet "Tenants" pour en créer un.' }
          ]
        },
        {
          id: 'navigation-platform',
          title: 'Navigation dans la Platform Admin',
          description: 'La Platform Admin est organisee en 5 onglets principaux, chacun dédié a un aspect de la gestion de la plateforme.',
          whenToUse: 'Utilisez cette section comme référence pour savoir ou trouver chaque fonctionnalite.',
          steps: [
            'Supervision : Tableau de bord avec KPIs globaux et sante des tenants. C\'est votre page d\'accueil.',
            'Tenants : Liste complete des clients, creation, modification, suspension et réactivation.',
            'Diagnostic : Analyse technique categorisee de chaque tenant (configuration, permissions, donnees).',
            'Assistant IA : Outil d\'analyse intelligente propulse par GPT qui identifié les problemes et propose des corrections.',
            'Journal : Historique complet de toutes les interventions effectuées par les administrateurs.'
          ],
          result: 'Chaque onglet vous donne accès a des outils spécifiques pour gerer efficacement la plateforme.'
        }
      ]
    },

    /* ============================================================
       2. SUPERVISION
    ============================================================ */
    {
      id: 'supervision',
      title: 'Tableau de bord Supervision',
      overview: 'Surveillez l\'activité globale de la plateforme et l\'état de sante de chaque tenant en un coup d\'oeil.',
      content: [
        {
          id: 'kpis-globaux',
          title: 'KPIs globaux',
          description: 'La partie superieure affiche 6 indicateurs cles : nombre total de tenants, tenants actifs, tenants suspendus, nombre total d\'utilisateurs, nombre total d\'employés, et nombre de problemes détectés.',
          whenToUse: 'Consultez les KPIs chaque matin pour avoir une vue rapide de l\'état de la plateforme.',
          steps: [
            'Accédez a la Platform Admin via le menu lateral.',
            'L\'onglet "Supervision" est affiche par défaut.',
            'Les 6 cartes KPI en haut de page montrent les compteurs en temps reel.',
            'Un indicateur rouge sur "Problemes" signale des problemes critiques nécessitant une attention immediate.'
          ],
          screenshot: '/docs/platform-supervision-kpis.png',
          screenshotAlt: 'KPIs de supervision de la plateforme',
          result: 'Vous avez une vision instantanee de la sante globale de la plateforme.',
          tips: ['Cliquez sur le bouton "Rafraichir" pour mettre a jour les KPIs en temps reel.'],
          errors: [
            { problem: 'Les KPIs affichent 0 partout', solution: 'Vérifiez qu\'au moins un tenant actif existe avec des employés. Les KPIs sont calcules a partir des donnees reelles.' }
          ]
        },
        {
          id: 'sante-tenants',
          title: 'Sante des tenants',
          description: 'Le tableau de sante affiche chaque tenant avec un indicateur visuel (vert, ambre, rouge) base sur les problemes détectés automatiquement.',
          whenToUse: 'Utilisez cette vue pour identifier rapidement les tenants qui ont besoin d\'attention.',
          steps: [
            'Dans l\'onglet Supervision, descendez sous les KPIs pour voir le tableau.',
            'Chaque ligne représente un tenant avec : nom, statut (actif/suspendu), nombre d\'utilisateurs, d\'employés et indicateur de sante.',
            'Vert = Aucun probleme détecté.',
            'Ambre = Avertissements mineurs (configuration incomplete, etc.).',
            'Rouge = Problemes critiques nécessitant une intervention.',
            'Cliquez sur "Details" pour voir le diagnostic détaillé du tenant.',
            'Cliquez sur "Intervenir" pour entrer en mode support sur ce tenant.'
          ],
          screenshot: '/docs/platform-tenant-health.png',
          screenshotAlt: 'Tableau de sante des tenants',
          result: 'Vous identifiez immédiatement les tenants problematiques et pouvez agir en consequence.',
          warnings: ['Les tenants marques comme "Demo" ou "Proteges" ne peuvent pas etre supprimés. Cela protege les donnees de demonstration.']
        }
      ]
    },

    /* ============================================================
       3. GESTION DES TENANTS
    ============================================================ */
    {
      id: 'gestion-tenants',
      title: 'Gestion des tenants',
      overview: 'Créer, modifier, suspendre et réactiver les comptes clients de la plateforme.',
      content: [
        {
          id: 'créer-tenant',
          title: 'Créer un nouveau tenant',
          description: 'Un tenant représente un client (entreprise) dans PunchTM. Chaque tenant est completement isole : ses employés, sites, zones, pointages et configurations sont independants des autres tenants.',
          whenToUse: 'Lorsqu\'un nouveau client souscrit a PunchTM et doit etre configure pour la premiere fois.',
          steps: [
            'Accédez a l\'onglet "Tenants" dans la Platform Admin.',
            'Cliquez sur le bouton "+ Nouveau tenant" en haut a droite.',
            'Remplissez les informations obligatoires : Nom de l\'entreprise, Slug (identifiant unique, sans espaces).',
            'Remplissez les informations du superuser initial : Prenom, Nom, Email, Mot de passe.',
            'Ce superuser sera l\'administrateur principal du tenant et pourra créer d\'autres utilisateurs.',
            'Cliquez sur "Créer" pour finaliser.',
            'Le tenant apparait immédiatement dans la liste avec le statut "Actif".'
          ],
          screenshot: '/docs/platform-create-tenant.png',
          screenshotAlt: 'Formulaire de creation d\'un nouveau tenant',
          result: 'Le tenant est créé, son superuser peut se connecter immédiatement avec les identifiants fournis.',
          tips: [
            'Le slug doit etre unique et ne contenir que des lettres minuscules, chiffres et tirets. Il est utilise en interne pour identifier le tenant.',
            'Choisissez un mot de passe fort pour le superuser initial. Il pourra le changer depuis son profil.'
          ],
          errors: [
            { problem: 'Erreur "Slug déjà utilise"', solution: 'Choisissez un slug different. Chaque tenant doit avoir un identifiant unique.' },
            { problem: 'Erreur "Email déjà utilise"', solution: 'L\'email du superuser est déjà associe a un autre compte. Utilisez une adresse differente.' }
          ]
        },
        {
          id: 'modifier-tenant',
          title: 'Modifier un tenant',
          description: 'Modifiez les informations d\'un tenant existant : nom, statut, paramètres de configuration.',
          whenToUse: 'Lorsqu\'un client change de nom d\'entreprise ou que vous devez ajuster ses paramètres.',
          steps: [
            'Dans la liste des tenants, cliquez sur la ligne du tenant a modifier.',
            'Le panneau de détails s\'ouvre sur la droite.',
            'Cliquez sur le bouton "Modifier" pour ouvrir le formulaire d\'edition.',
            'Modifiez les champs nécessaires.',
            'Cliquez sur "Enregistrer" pour sauvegarder les modifications.'
          ],
          result: 'Les modifications sont appliquees immédiatement. Les utilisateurs du tenant verront les changements a leur prochaine connexion.',
          warnings: ['Le slug ne peut pas etre modifié apres la creation pour éviter de casser les références internes.']
        },
        {
          id: 'suspendre-réactiver',
          title: 'Suspendre / Réactiver un tenant',
          description: 'La suspension bloque l\'accès de tous les utilisateurs d\'un tenant sans supprimer ses donnees. La réactivation restaure l\'accès.',
          whenToUse: 'Suspension : lorsqu\'un client ne paie plus ou en cas de litige. Reactivation : lorsque la situation est résolue.',
          steps: [
            'Dans la liste des tenants, trouvez le tenant concerne.',
            'Cliquez sur le bouton "Suspendre" (icone pause) a droite de la ligne.',
            'Confirmez l\'action dans la boite de dialogue.',
            'Le tenant passe en statut "Suspendu" (affiche en rouge).',
            'Pour réactiver : cliquez sur "Réactiver" (icone lecture) puis confirmez.'
          ],
          result: 'Suspension : tous les utilisateurs du tenant recoivent un message d\'erreur lors de leur prochaine tentative de connexion. Reactivation : l\'accès est restaure immédiatement.',
          warnings: [
            'Les tenants proteges (ex: Antarogeomatik) ne peuvent pas etre suspendus. Cette protection est volontaire pour les comptes de demonstration.',
            'La suspension ne supprimé aucune donnee. Toutes les informations sont preservees.'
          ],
          errors: [
            { problem: 'Le bouton "Suspendre" est grise', solution: 'Ce tenant est marque comme protege. Il ne peut pas etre suspendu.' }
          ]
        },
        {
          id: 'reinitialiser-accès',
          title: 'Reinitialiser l\'accès admin',
          description: 'Si le superuser d\'un tenant perd son mot de passe et que la procédure normale ne fonctionne pas, vous pouvez reinitialiser son accès depuis la Platform Admin.',
          whenToUse: 'Uniquement en dernier recours, lorsque le client ne peut plus accéder a son compte par aucun autre moyen.',
          steps: [
            'Ouvrez le panneau de détails du tenant concerne.',
            'Cliquez sur "Reinitialiser accès admin".',
            'Un nouveau mot de passe est généré automatiquement.',
            'Copiez ce mot de passe et transmettez-le au client de maniere sécurisée.',
            'L\'action est journalisee dans le Journal des interventions.'
          ],
          result: 'Le superuser du tenant peut se connecter avec le nouveau mot de passe. L\'ancien mot de passe est invalide.',
          warnings: ['Cette action est journalisee et doit etre utilisee uniquement en cas de nécessite. Privilegiez toujours la procédure standard de reset de mot de passe.']
        }
      ]
    },

    /* ============================================================
       4. MODE SUPPORT
    ============================================================ */
    {
      id: 'mode-support',
      title: 'Mode Support',
      overview: 'Intervenez directement sur le compte d\'un tenant en mode sécurisé et audite.',
      content: [
        {
          id: 'entrer-support',
          title: 'Entrer en mode Support',
          description: 'Le mode Support vous permet de vous connecter temporairement sur le compte d\'un tenant pour diagnostiquer et resoudre un probleme. Toutes vos actions sont enregistrees.',
          whenToUse: 'Lorsqu\'un client signale un probleme que vous devez reproduire ou corriger directement dans son espace.',
          steps: [
            'Dans l\'onglet Supervision, trouvez le tenant concerne.',
            'Cliquez sur le bouton "Intervenir" a cote du tenant.',
            'Une boite de dialogue vous demande la raison de l\'intervention. Saisissez une description claire (ex: "Client signale un probleme de pointage pour l\'employé 10005").',
            'Cliquez sur "Confirmer l\'intervention".',
            'Une banniere bleue apparait en haut de l\'ecran indiquant "Mode Support - Intervention sur [nom du tenant]".',
            'Vous etes maintenant connecte avec les droits du superuser de ce tenant.',
            'Naviguez dans les differents modules pour diagnostiquer le probleme.'
          ],
          screenshot: '/docs/platform-support-mode.png',
          screenshotAlt: 'Banniere Mode Support active',
          result: 'Vous avez accès a toutes les fonctionnalites du tenant concerne. Toutes vos actions sont tracees dans le journal des interventions.',
          warnings: [
            'N\'effectuez que les modifications nécessaires pour resoudre le probleme signale.',
            'Ne modifiez jamais les donnees de paie verrouillee sans l\'accord explicite du client.',
            'La raison de l\'intervention est obligatoire et sera visible dans le journal d\'audit.'
          ]
        },
        {
          id: 'quitter-support',
          title: 'Quitter le mode Support',
          description: 'Lorsque votre intervention est terminee, quittez proprement le mode Support pour revenir a votre session Super Admin.',
          whenToUse: 'Dès que votre intervention est terminee.',
          steps: [
            'Cliquez sur le bouton "Quitter le mode support" dans la banniere bleue en haut de l\'ecran.',
            'Vous etes redirige automatiquement vers la Platform Admin.',
            'L\'intervention est marquee comme terminee dans le journal avec la duree totale.'
          ],
          result: 'Vous retrouvez votre session Super Admin. L\'intervention est clôturée et documentée.',
          tips: ['Si vous vous deconnectez au lieu de quitter le mode support, l\'intervention restera marquee comme "En cours" dans le journal.']
        }
      ]
    },

    /* ============================================================
       5. DIAGNOSTIC
    ============================================================ */
    {
      id: 'diagnostic',
      title: 'Diagnostic systeme',
      overview: 'Analysez la configuration et les donnees de chaque tenant pour identifier les problemes potentiels.',
      content: [
        {
          id: 'catégories-diagnostic',
          title: 'Catégories de diagnostic',
          description: 'Le diagnostic analyse chaque tenant selon 5 catégories : Configuration, Permissions, Donnees manquantes, Paie et Planification. Chaque probleme détecté est classe par sévérité (Critique, Avertissement, Information).',
          whenToUse: 'Lorsque vous voulez vérifier la bonne configuration d\'un tenant ou comprendre pourquoi certaines fonctionnalites ne fonctionnent pas correctement.',
          steps: [
            'Accédez a l\'onglet "Diagnostic" dans la Platform Admin.',
            'Sélectionnez un tenant dans la liste deroulante.',
            'Le diagnostic s\'execute automatiquement et affiche les résultats par catégorie.',
            'Rouge (Critique) : Probleme bloquant qui empeche l\'utilisation normale. Action immediate requise.',
            'Ambre (Avertissement) : Probleme non bloquant mais qui peut causer des dysfonctionnements. A corriger rapidement.',
            'Bleu (Information) : Suggestion d\'amelioration ou configuration optionnelle manquante.',
            'Chaque probleme affiche une suggestion de correction que vous pouvez suivre.'
          ],
          screenshot: '/docs/platform-diagnostic.png',
          screenshotAlt: 'Resultats de diagnostic d\'un tenant',
          result: 'Vous avez une vision complete de la sante technique du tenant avec des actions concretes pour chaque probleme.',
          tips: [
            'Vérifiez le diagnostic apres chaque nouvelle configuration de tenant pour vous assurer que tout est correct.',
            'Le pourcentage de complétude indique le niveau de configuration du tenant (100% = tout est configure).'
          ]
        },
        {
          id: 'exemples-problemes',
          title: 'Exemples de problemes détectés',
          description: 'Voici les types de problemes que le diagnostic peut identifier et comment les resoudre.',
          whenToUse: 'Comme référence lorsque vous rencontrez un probleme dans les résultats de diagnostic.',
          steps: [
            'Configuration : "Aucun site configure" -> Créez au moins un site dans le module Sites & Zones du tenant.',
            'Configuration : "Aucune zone configuree" -> Créez des zones et assignez-les a des sites.',
            'Configuration : "Aucun quart de travail" -> Créez des quarts dans le module Quarts.',
            'Permissions : "Utilisateurs sans zone assignee" -> Assignez des zones aux superviseurs.',
            'Donnees : "Employés sans site" -> Modifiez les fiches employés pour assigner un site.',
            'Paie : "Periodes de paie non configurees" -> Créez des périodes de paie dans le module Paie/RH.',
            'Planification : "Employés sans quart planifie" -> Utilisez le module Planification pour affecter des quarts.'
          ],
          result: 'En suivant les suggestions, le score de sante du tenant s\'ameliore progressivement vers 100%.'
        }
      ]
    },

    /* ============================================================
       6. ASSISTANT IA
    ============================================================ */
    {
      id: 'assistant-ia',
      title: 'Assistant IA',
      overview: 'Utilisez l\'intelligence artificielle pour analyser automatiquement les tenants et appliquer des corrections.',
      content: [
        {
          id: 'analyse-ia',
          title: 'Lancer une analyse IA',
          description: 'L\'Assistant IA utilise GPT pour analyser en profondeur les donnees d\'un tenant. Il détecté les incoherences, les problemes de configuration et les anomalies que le diagnostic standard ne couvre pas.',
          whenToUse: 'Apres un diagnostic standard, pour obtenir une analyse plus approfondie. Ou lorsque vous ne trouvez pas la cause d\'un probleme signale par un client.',
          steps: [
            'Accédez a l\'onglet "Assistant IA" dans la Platform Admin.',
            'Sélectionnez un tenant dans le menu deroulant.',
            'Cliquez sur le bouton "Analyser" (avec l\'icone etincelle).',
            'L\'analyse prend quelques secondes. Un indicateur de chargement s\'affiche.',
            'Le résultat s\'affiche avec : un Score de sante (0-100), des Problemes groupes par sévérité, et des Recommandations.',
            'Chaque probleme identifié inclut : description, cause probable, impact metier et action recommandee.'
          ],
          screenshot: '/docs/platform-ai-analysis.png',
          screenshotAlt: 'Résultat d\'analyse IA d\'un tenant',
          result: 'Vous obtenez une analyse détaillée avec des recommandations concretes, classees par priorite.',
          tips: [
            'L\'IA ne collecte que des donnees non sensibles (compteurs, flags, incoherences). Elle n\'accede jamais aux donnees personnelles des employés.',
            'Lancez une analyse IA apres chaque intervention corrective pour vérifier l\'amelioration.'
          ],
          warnings: ['L\'analyse IA nécessite une connexion internet et consomme des credits API. Utilisez-la de maniere raisonnee.']
        },
        {
          id: 'corrections-ia',
          title: 'Appliquer des corrections',
          description: 'Pour certains problemes simples, l\'IA propose un bouton "Corriger" qui applique automatiquement la correction. Les actions complexes ou sensibles (paie, suppressions) ne sont jamais proposees en correction automatique.',
          whenToUse: 'Lorsque l\'analyse IA affiche un bouton "Corriger" a cote d\'un probleme. Uniquement pour des corrections mineures et non destructives.',
          steps: [
            'Apres une analyse IA, identifiez les problemes avec un bouton "Corriger".',
            'Cliquez sur "Corriger" a cote du probleme.',
            'Une boite de dialogue de confirmation s\'affiche avec le détail de l\'action qui sera effectuée.',
            'Vérifiez que l\'action est appropriee et cliquez sur "Confirmer".',
            'L\'IA applique la correction et confirme le résultat.',
            'L\'action est journalisee dans le Journal des interventions.'
          ],
          result: 'La correction est appliquee immédiatement. Le probleme disparait de la prochaine analyse.',
          warnings: [
            'Les corrections IA sont limitees aux actions sures : assigner un site, un quart ou une zone, desactiver des comptes de test.',
            'Les actions sensibles (modification de paie, suppression de donnees, changement de permissions) ne sont jamais proposees en correction automatique.'
          ]
        },
        {
          id: 'historique-analyses',
          title: 'Historique des analyses',
          description: 'Chaque analyse IA est sauvegardee avec son score et ses résultats. Le tableau historique vous permet de suivre l\'evolution de la sante d\'un tenant dans le temps.',
          whenToUse: 'Pour suivre l\'evolution d\'un tenant au fil des interventions et vérifier que les corrections portent leurs fruits.',
          steps: [
            'Dans l\'onglet Assistant IA, sélectionnez un tenant.',
            'Le tableau historique s\'affiche sous la zone d\'analyse.',
            'Chaque ligne montre : date, score de sante, nombre de problemes critiques/avertissements/infos.',
            'Un badge Delta indique l\'evolution par rapport a l\'analyse précédente (vert = amelioration, rouge = dégradation).',
            'La dernière analyse est marquee d\'un badge "Dernière".'
          ],
          result: 'Vous visualisez clairement l\'evolution de la sante du tenant et l\'impact de vos interventions.',
          tips: ['Si le score se degrade malgre vos corrections, vérifiez que le client n\'a pas modifié sa configuration entre-temps.']
        }
      ]
    },

    /* ============================================================
       7. JOURNAL DES INTERVENTIONS
    ============================================================ */
    {
      id: 'journal',
      title: 'Journal des interventions',
      overview: 'Traçabilité complete de toutes les actions des administrateurs sur la plateforme.',
      content: [
        {
          id: 'consulter-journal',
          title: 'Consulter le journal',
          description: 'Le journal enregistre automatiquement toutes les actions critiques : interventions en mode support, analyses IA, corrections appliquees, suspensions/reactivations, reinitialisations d\'accès.',
          whenToUse: 'Pour l\'audit, la traçabilité ou pour comprendre un changement inattendu dans les donnees d\'un tenant.',
          steps: [
            'Accédez a l\'onglet "Journal" dans la Platform Admin.',
            'Le journal affiche les interventions les plus recentes en haut.',
            'Chaque entree montre : l\'administrateur, la date/heure, le tenant concerne, le type d\'action, la raison et la duree.',
            'Les interventions en mode support affichent aussi la duree totale de l\'intervention.',
            'Les analyses et corrections IA sont identifiées par un badge spécifique.'
          ],
          result: 'Vous avez une traçabilité complete de toutes les actions administratives sur la plateforme.',
          tips: ['Le journal est non modifiable. Aucune entree ne peut etre supprimee ou altérée pour garantir l\'intégrité de l\'audit.']
        }
      ]
    },

    /* ============================================================
       8. SECURITE ET BONNES PRATIQUES
    ============================================================ */
    {
      id: 'sécurité',
      title: 'Securite et bonnes pratiques',
      overview: 'Recommandations pour une gestion sécurisée et efficace de la plateforme.',
      content: [
        {
          id: 'bonnes-pratiques-sécurité',
          title: 'Bonnes pratiques de sécurité',
          description: 'Regles essentielles pour maintenir la sécurité de la plateforme PunchTM.',
          whenToUse: 'A lire et appliquer en permanence.',
          steps: [
            'Utilisez un mot de passe fort et unique pour votre compte Super Admin (minimum 12 caracteres, majuscules, minuscules, chiffres, caracteres speciaux).',
            'Ne partagez jamais vos identifiants Super Admin. Chaque administrateur doit avoir son propre compte.',
            'Documentez toujours la raison de vos interventions en mode support.',
            'Vérifiez le journal d\'audit regulierement pour detecter des activites inhabituelles.',
            'Effectuez un diagnostic de tous les tenants au moins une fois par semaine.',
            'Apres une intervention, quittez toujours proprement le mode support.'
          ],
          result: 'Une plateforme sécurisée, tracable et conforme aux bonnes pratiques.',
          warnings: [
            'Ne supprimez jamais un tenant sans avoir vérifié qu\'il n\'y a plus de donnees critiques non exportees.',
            'Ne reinitialisez jamais l\'accès admin d\'un tenant sans avoir essaye les autres solutions (reset mot de passe standard).'
          ]
        },
        {
          id: 'gestion-quotidienne',
          title: 'Routine de gestion quotidienne',
          description: 'Checklist quotidienne recommandee pour un Super Admin.',
          whenToUse: 'Chaque matin ou debut de journee de travail.',
          steps: [
            'Connectez-vous et vérifiez les KPIs de supervision (nombre de problemes en rouge).',
            'Consultez le tableau de sante des tenants. Traitez les tenants en rouge en priorite.',
            'Vérifiez le journal des interventions pour les actions recentes des autres administrateurs.',
            'Si un tenant a des problemes critiques, lancez une analyse IA pour obtenir un diagnostic approfondi.',
            'Traitez les demandes de support client en utilisant le mode Support si nécessaire.',
            'En fin de journee, vérifiez que toutes vos interventions sont bien cloturees.'
          ],
          result: 'Une plateforme bien supervisee avec des problemes détectés et traites rapidement.',
          tips: ['Créez un calendrier de diagnostics hebdomadaires pour chaque tenant afin de prevenir les problemes avant qu\'ils ne surviennent.']
        }
      ]
    }
  ]
};
